(() => {
  class CampaignEngine {
    constructor() {
      this.config = this.readJsonScript('CampaignEngineConfig');
      this.rules = this.readJsonScript('CampaignEngineRules') || [];
      this.activeCampaign = null;
      this.observer = null;
      this.renderScheduled = false;
    }

    init() {
      if (!this.config?.enabled) return;
      if (!Array.isArray(this.rules) || this.rules.length === 0) return;

      this.rules = this.rules
        .filter(Boolean)
        .filter((campaign) => campaign.campaign_key)
        .sort((a, b) => (Number(b.priority) || 0) - (Number(a.priority) || 0));

      this.activeCampaign = this.resolveCampaign();

      if (!this.activeCampaign) {
        this.log('No campaign matched.');
        return;
      }

      this.markDomState(this.activeCampaign);
      this.persistCampaignState(this.activeCampaign);

      this.syncCartState(this.activeCampaign)
        .then(() => {
          this.renderAllPriceContainers();
          this.observeDom();
        })
        .catch((error) => {
          this.log('Cart sync failed', error);
          this.renderAllPriceContainers();
          this.observeDom();
        });
    }

    readJsonScript(id) {
      const node = document.getElementById(id);
      if (!node) return null;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error(`[CampaignEngine] Failed to parse ${id}`, error);
        return null;
      }
    }

    log(...args) {
      if (!this.config?.debug) return;
      console.info('[CampaignEngine]', ...args);
    }

    /** @returns {'code' | 'global'} */
    getCampaignType(campaign) {
      const t = String(campaign?.campaign_type || 'code').toLowerCase();
      return t === 'global' ? 'global' : 'code';
    }

    /** UTM keys used by code campaigns (for stale-persistence detection). Global rules are excluded. */
    collectTrackedUtmKeys() {
      const keys = new Set();
      for (const r of this.rules) {
        if (this.getCampaignType(r) === 'global') continue;
        if (r.utm_parameter) keys.add(String(r.utm_parameter));
      }
      return keys;
    }

    resolveCampaign() {
      const matchedFromQuery = this.findMatchingCampaignFromQuery();
      if (matchedFromQuery) return matchedFromQuery;

      // Tracked UTM param is non-empty but no rule matched → skip storage for *this* page
      // only (e.g. ?utm_campaign=newsletter). Avoids showing Rebuy prices while URL implies
      // a different campaign context. Next navigation without UTMs can still use storage.
      const params = new URLSearchParams(window.location.search);
      for (const key of this.collectTrackedUtmKeys()) {
        const raw = params.get(key);
        if (raw != null && String(raw).trim() !== '') {
          this.log('Tracked UTM present but no rule matched — ignoring persisted campaign', key, raw);
          return null;
        }
      }

      const persisted = this.readPersistedCampaignIfStillValid();
      if (persisted) return persisted;

      return this.findActiveGlobalCampaign();
    }

    findMatchingCampaignFromQuery() {
      const params = new URLSearchParams(window.location.search);
      const now = new Date();

      const matches = this.rules.filter((campaign) => {
        if (this.getCampaignType(campaign) === 'global') return false;
        if (campaign.is_active === false) return false;
        if (!campaign.utm_parameter || !campaign.match_type || campaign.match_value == null) return false;
        if (!this.isWithinSchedule(campaign, now)) return false;

        const paramValue = params.get(campaign.utm_parameter);
        if (!paramValue) return false;

        const normalizedParam = String(paramValue).toLowerCase();
        const normalizedMatch = String(campaign.match_value).toLowerCase();

        if (campaign.match_type === 'equals') {
          return normalizedParam === normalizedMatch;
        }

        if (campaign.match_type === 'contains') {
          return normalizedParam.includes(normalizedMatch);
        }

        return false;
      });

      if (!matches.length) return null;

      const winningCampaign = { ...matches[0] };
      winningCampaign._matched_from = 'query';
      winningCampaign._matched_param_value = params.get(winningCampaign.utm_parameter) || '';

      this.log('Matched campaign from query', winningCampaign);
      return winningCampaign;
    }

    findActiveGlobalCampaign() {
      const now = new Date();
      const globals = this.rules.filter((campaign) => {
        if (this.getCampaignType(campaign) !== 'global') return false;
        if (campaign.is_active === false) return false;
        if (!this.isWithinSchedule(campaign, now)) return false;
        return true;
      });

      if (!globals.length) return null;

      const winningCampaign = { ...globals[0] };
      winningCampaign._matched_from = 'global_fallback';
      winningCampaign._matched_param_value = '';

      this.log('Matched global fallback campaign', winningCampaign);
      return winningCampaign;
    }

    isWithinSchedule(campaign, now) {
      const startsAt = campaign.starts_at ? new Date(campaign.starts_at) : null;
      const endsAt = campaign.ends_at ? new Date(campaign.ends_at) : null;

      if (startsAt && now < startsAt) return false;
      if (endsAt && now > endsAt) return false;

      return true;
    }

    getCodeAttributionCookieName() {
      return this.config?.code_attribution_cookie || 'discount_campaign_code_attr_session_v1';
    }

    /** Mirrors code-campaign attribution when sessionStorage is cleared or unavailable. */
    setCodeAttributionCookie(state) {
      const name = this.getCodeAttributionCookieName();
      const payload = encodeURIComponent(
        JSON.stringify({
          campaign_key: state.campaign_key,
          matched_value: state.matched_value || '',
          persisted_at: state.persisted_at
        })
      );
      // Session cookie: omit Max-Age and Expires (cleared when the browser session ends).
      document.cookie = `${name}=${payload};path=/;SameSite=Lax`;
    }

    clearCodeAttributionCookie() {
      const name = this.getCodeAttributionCookieName();
      document.cookie = `${name}=;path=/;max-age=0;SameSite=Lax`;
    }

    readCodeAttributionFromCookie() {
      const name = this.getCodeAttributionCookieName();
      const part = document.cookie.split(';').map((s) => s.trim()).find((s) => s.startsWith(`${name}=`));
      if (!part) return null;
      try {
        return JSON.parse(decodeURIComponent(part.slice(name.length + 1)));
      } catch (error) {
        this.log('Cookie parse failed', error);
        return null;
      }
    }

    persistCampaignState(campaign) {
      if (!this.config?.storage_key) return;

      const type = this.getCampaignType(campaign);
      const state = {
        campaign_key: campaign.campaign_key,
        discount_code: campaign.discount_code,
        campaign_type: type,
        matched_from: campaign._matched_from || 'query',
        matched_value: campaign._matched_param_value || '',
        persisted_at: Date.now()
      };

      try {
        window.sessionStorage.setItem(this.config.storage_key, JSON.stringify(state));
      } catch (error) {
        this.log('sessionStorage persist failed', error);
      }

      if (type === 'code') {
        this.setCodeAttributionCookie(state);
      } else {
        this.clearCodeAttributionCookie();
      }
    }

    hydrateCampaignFromPersisted(persisted) {
      const campaign = this.rules.find((rule) => rule.campaign_key === persisted.campaign_key);
      if (!campaign) return null;
      if (campaign.is_active === false) return null;
      if (!this.isWithinSchedule(campaign, new Date())) return null;

      return {
        ...campaign,
        _matched_from: 'storage',
        _matched_param_value: persisted.matched_value || ''
      };
    }

    readPersistedCampaignIfStillValid() {
      if (!this.config?.storage_key) return null;

      try {
        const raw = window.sessionStorage.getItem(this.config.storage_key);
        if (raw) {
          const persisted = JSON.parse(raw);
          if (persisted?.campaign_key) {
            const hydrated = this.hydrateCampaignFromPersisted(persisted);
            if (hydrated) return hydrated;
            window.sessionStorage.removeItem(this.config.storage_key);
          }
        }
      } catch (error) {
        this.log('sessionStorage read failed', error);
      }

      const fromCookie = this.readCodeAttributionFromCookie();
      if (!fromCookie?.campaign_key) return null;

      const hydrated = this.hydrateCampaignFromPersisted(fromCookie);
      if (!hydrated) {
        this.clearCodeAttributionCookie();
        return null;
      }

      if (this.getCampaignType(hydrated) === 'global') {
        this.clearCodeAttributionCookie();
        return null;
      }

      this.log('Restored code campaign from cookie fallback', hydrated.campaign_key);
      return hydrated;
    }

    markDomState(campaign) {
      document.documentElement.classList.add('campaign-active');
      document.documentElement.dataset.campaignKey = campaign.campaign_key || '';
      document.documentElement.dataset.campaignDisplayMode = campaign.display_mode || '';
      document.body.dataset.campaignKey = campaign.campaign_key || '';
    }

    verifyDiscountApplied(cart, discountCode) {
      if (!cart || !discountCode) return false;

      const needle = String(discountCode).toLowerCase();

      const hasMatchingCode = Array.isArray(cart.discount_codes) &&
        cart.discount_codes.some(
          (d) => d.applicable === true && String(d.code).toLowerCase() === needle
        );
      if (hasMatchingCode) return true;

      const hasCartLevelDiscount = Array.isArray(cart.cart_level_discount_applications) &&
        cart.cart_level_discount_applications.length > 0;
      if (hasCartLevelDiscount) return true;

      if (cart.total_discount && Number(cart.total_discount) > 0) return true;

      return false;
    }

    async pollCartForDiscount(discountCode, maxAttempts = 4, delayMs = 300) {
      let cart = null;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        cart = await response.json();

        if (this.verifyDiscountApplied(cart, discountCode)) {
          this.log('Discount verified on attempt', attempt + 1);
          return cart;
        }
      }

      this.log('Discount not detected after polling — may apply at checkout');
      return cart;
    }

    async refreshCartDrawer(cart) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      const sectionId = drawer.getAttribute('data-section-id');
      if (!sectionId) return;

      const rootUrl = window.FoxTheme?.routes?.root_url
        || window.Shopify?.routes?.root || '/';
      const cartId = `CartDrawer-${sectionId}`;
      const cartElement = document.getElementById(cartId);
      if (!cartElement) return;

      try {
        const response = await fetch(`${rootUrl}?section_id=${sectionId}`);
        const responseText = await response.text();
        const parsed = new DOMParser().parseFromString(responseText, 'text/html');
        const newContent = parsed.getElementById(cartId);
        if (newContent) {
          cartElement.innerHTML = newContent.innerHTML;
        }
      } catch (error) {
        this.log('Cart drawer refresh failed', error);
      }

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart } })
      );
    }

    async syncCartState(campaign) {
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            _campaign_key: campaign.campaign_key || '',
            _campaign_discount_code: campaign.discount_code || '',
            _campaign_source: campaign.utm_parameter || '',
            _campaign_match_value: campaign._matched_param_value || ''
          }
        })
      });

      if (!campaign.discount_code) return;

      const discountStateKey = `campaign-discount-applied:${campaign.campaign_key}`;
      if (window.sessionStorage.getItem(discountStateKey) === '1') return;

      window.sessionStorage.setItem(discountStateKey, '1');

      const discountUrl = `/discount/${encodeURIComponent(campaign.discount_code)}`;
      await fetch(discountUrl, { method: 'GET', credentials: 'same-origin' });

      const cart = await this.pollCartForDiscount(campaign.discount_code);
      await this.refreshCartDrawer(cart);
    }

    observeDom() {
      if (this.observer) return;

      this.observer = new MutationObserver(() => {
        if (this.renderScheduled) return;

        this.renderScheduled = true;
        window.requestAnimationFrame(() => {
          this.renderScheduled = false;
          this.renderAllPriceContainers();
        });
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    renderAllPriceContainers() {
      const containers = document.querySelectorAll('[data-price-container]');
      containers.forEach((container) => this.renderPriceContainer(container));
    }

    renderPriceContainer(container) {
      if (!this.activeCampaign || !container) return;

      const productId = Number(container.dataset.productId || 0);
      const collectionIds = this.parseIds(container.dataset.productCollectionIds);
      const previewSafe = container.dataset.previewSafe === 'true';
      const priceScope = container.dataset.priceScope || 'default';

      const applies = this.campaignAppliesToProduct(this.activeCampaign, productId, collectionIds);
      if (!applies) {
        this.clearCampaignSaleInjections(container);
        this.toggleCampaignBadge(container, false);
        return;
      }

      const displayMode = this.activeCampaign.display_mode || 'price_preview';
      const supportsPreview = this.supportsSimplePreview(this.activeCampaign);

      if (displayMode === 'cart_only') {
        this.clearCampaignSaleInjections(container);
        this.toggleCampaignBadge(container, false);
        return;
      }

      if (displayMode === 'badge_only') {
        this.clearCampaignSaleInjections(container);
        this.toggleCampaignBadge(container, false);
        return;
      }

      if (priceScope === 'product-card' && !previewSafe) {
        this.clearCampaignSaleInjections(container);
        this.toggleCampaignBadge(container, false);
        return;
      }

      if (!supportsPreview) {
        this.clearCampaignSaleInjections(container);
        this.toggleCampaignBadge(container, false);
        return;
      }

      const basePrice = Number(container.dataset.basePrice || 0);
      const baseCompareAtPrice = Number(container.dataset.baseCompareAtPrice || 0);
      if (!basePrice) {
        this.clearCampaignSaleInjections(container);
        return;
      }

      const effectivePrice = this.calculatePreviewPrice(basePrice, this.activeCampaign);
      if (effectivePrice == null) {
        this.clearCampaignSaleInjections(container);
        return;
      }

      const effectiveCompareAtPrice = baseCompareAtPrice > basePrice ? baseCompareAtPrice : basePrice;

      this.clearCampaignSaleInjections(container);
      this.updatePriceMarkup(container, {
        effectivePrice,
        effectiveCompareAtPrice
      });
      this.toggleCampaignBadge(container, false);
    }

    parseIds(value) {
      return String(value || '')
        .split(',')
        .map((item) => Number(item.trim()))
        .filter(Boolean);
    }

    campaignAppliesToProduct(campaign, productId, collectionIds) {
      if (campaign.applies_to_all_products) return true;

      const productTargets = Array.isArray(campaign.products) ? campaign.products : [];
      const productTargetSet = new Set();
      for (const p of productTargets) {
        const n = Number(p);
        if (Number.isFinite(n) && !Number.isNaN(n)) productTargetSet.add(n);
        productTargetSet.add(String(p).trim());
      }
      const pidNum = Number(productId);
      const pidStr = String(productId);
      if (productTargetSet.has(pidNum) || productTargetSet.has(pidStr)) return true;

      const collectionTargets = Array.isArray(campaign.collections) ? campaign.collections.map(Number) : [];
      if (collectionTargets.length && collectionIds.some((id) => collectionTargets.includes(id))) return true;

      return false;
    }

    supportsSimplePreview(campaign) {
      return campaign.preview_discount_type === 'percentage' || campaign.preview_discount_type === 'fixed_amount';
    }

    calculatePreviewPrice(basePrice, campaign) {
      const type = campaign.preview_discount_type;
      const value = Number(campaign.preview_discount_value || 0);
      if (!value) return null;

      if (type === 'percentage') {
        return Math.max(0, Math.round(basePrice * (1 - value / 100)));
      }

      if (type === 'fixed_amount') {
        return Math.max(0, Math.round(basePrice - value * 100));
      }

      return null;
    }

    updatePriceMarkup(container, state) {
      const regularNode = container.querySelector('.f-price__regular .f-price-item--regular');
      const saleNode = container.querySelector('.f-price__sale .f-price-item--sale');
      const saleDiv = container.querySelector('.f-price__sale');
      let compareNode = container.querySelector('.f-price__sale .f-price-item--regular s');
      const unitPriceNode = container.querySelector('[data-unit-price]');

      const symbolOnly = container.dataset.priceScope === 'product-card';
      const formattedEffectivePrice = this.formatMoney(state.effectivePrice, symbolOnly);
      const formattedComparePrice = this.formatMoney(state.effectiveCompareAtPrice, symbolOnly);

      if (regularNode) regularNode.textContent = formattedEffectivePrice;
      if (saleNode) saleNode.textContent = formattedEffectivePrice;

      const hasDiscount = state.effectiveCompareAtPrice > state.effectivePrice;
      if (hasDiscount) {
        if (compareNode) {
          compareNode.textContent = formattedComparePrice;
        } else if (saleDiv) {
          const existingLabel = container.querySelector('.f-price__regular .visually-hidden--inline');
          const labelText = existingLabel ? existingLabel.textContent : 'Regular price';
          const visuallyHidden = document.createElement('span');
          visuallyHidden.className = 'visually-hidden visually-hidden--inline';
          visuallyHidden.textContent = labelText;
          const compareSpan = document.createElement('span');
          compareSpan.className = 'f-price-item f-price-item--regular';
          const sEl = document.createElement('s');
          sEl.textContent = formattedComparePrice;
          compareSpan.appendChild(sEl);
          saleDiv.appendChild(visuallyHidden);
          saleDiv.appendChild(compareSpan);
        }
      }

      const baseUnitPrice = parseInt(container.dataset.baseUnitPrice || '0', 10);
      const basePrice = parseInt(container.dataset.basePrice || '0', 10);
      if (unitPriceNode && baseUnitPrice > 0 && basePrice > 0) {
        const effectiveUnitPriceCents = Math.round(baseUnitPrice * (state.effectivePrice / basePrice));
        unitPriceNode.textContent = this.formatMoney(effectiveUnitPriceCents, symbolOnly);
      }

      container.dataset.effectivePrice = String(state.effectivePrice);
      container.dataset.effectiveCompareAtPrice = String(state.effectiveCompareAtPrice);
      container.dataset.campaignActive = 'true';
      container.dataset.campaignApplies = 'true';
      container.classList.add('is-campaign-price');
      container.classList.toggle('f-price--on-sale', hasDiscount);

      if (hasDiscount) {
        this.ensureProductCardSaleBadge(container);
      } else {
        this.clearCampaignSaleInjections(container);
      }
    }

    getOnSaleLabel() {
      const label = this.config?.on_sale_label;
      return label != null && String(label).trim() !== '' ? String(label) : 'Sale';
    }

    clearCampaignSaleInjections(container) {
      if (!container) return;
      const pid = String(container.dataset.productId || '');
      const removeMatches = (root) => {
        if (!root) return;
        const nodes = pid
          ? root.querySelectorAll(
              `[data-campaign-sale-badge="1"][data-campaign-sale-product="${CSS.escape(pid)}"]`
            )
          : root.querySelectorAll('[data-campaign-sale-badge="1"]');
        nodes.forEach((el) => el.remove());
      };

      removeMatches(container);
      removeMatches(container.closest('.product-card'));
      removeMatches(container.closest('product-info'));

      const badgesWrap = container.querySelector('.f-price__badges');
      if (badgesWrap && badgesWrap.children.length === 0) {
        badgesWrap.remove();
      }
    }

    hasNativeSaleBadge(root) {
      if (!root) return false;
      return Boolean(
        root.querySelector('.f-badge.f-badge--sale:not([data-campaign-sale-badge="1"])')
      );
    }

    ensureProductCardSaleBadge(container) {
      if (!this.config?.pcard_show_sale_badge) return;
      const card = container.closest('.product-card');
      const labels = card?.querySelector('.product-card__labels');
      if (!labels) return;
      if (this.hasNativeSaleBadge(labels)) return;

      const pid = String(container.dataset.productId || '');
      if (labels.querySelector(`[data-campaign-sale-badge="1"][data-campaign-sale-product="${CSS.escape(pid)}"]`)) {
        return;
      }

      const span = document.createElement('span');
      span.className = 'f-badge f-badge--sale';
      span.setAttribute('aria-hidden', 'true');
      span.setAttribute('data-campaign-sale-badge', '1');
      span.setAttribute('data-campaign-sale-product', pid);
      span.textContent = this.getOnSaleLabel();
      labels.appendChild(span);
    }

    toggleCampaignBadge(container, visible) {
      const badge = container.querySelector('[data-campaign-badge]');
      if (!badge) return;
      badge.hidden = !visible;
    }

    formatMoney(cents, symbolOnly = false) {
      const format =
        symbolOnly || !this.config.currency_code_enabled
          ? this.config.money_format
          : this.config.money_with_currency_format;

      if (window.FoxTheme?.Currency?.formatMoney) {
        return window.FoxTheme.Currency.formatMoney(cents, format);
      }

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, format);
      }

      return `${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  const boot = () => {
    const engine = new CampaignEngine();
    engine.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
